import { z } from 'zod';

export const AdminOMSPropertyOverridesSchema = z.array(
    z.object({
        id: z.string(),
        override: z.enum([
            'show-in-firm-info',
            'hide-in-firm-info',
            'show-in-account',
            'hide-in-account',
            'show-in-firm-risk',
            'hide-in-firm-risk',
            'required-in-account',
            'show-in-order-review',
        ]),
    }),
);

export type AdminOMSPropertyOverrides = z.infer<typeof AdminOMSPropertyOverridesSchema>;

/**
 * This is the initial set of overrides that is merged with the feature flag value.
 *
 * Overrides set by the feature flag will ALWAYS take precedence over these on a per-property basis.
 */
const INITIAL_ADMIN_OMS_PROPERTY_OVERRIDES: AdminOMSPropertyOverrides = [
    // CAT
    { id: 'cat_acc_type', override: 'hide-in-firm-risk' },
    { id: 'cat_acc_type', override: 'show-in-account' },
    { id: 'cat_industry_member_id', override: 'show-in-account' },
    { id: 'cat_firm_designated_id', override: 'show-in-account' },

    // Account Type
    { id: 'wb_account_type', override: 'hide-in-firm-risk' },
    // Account Class
    { id: 'wb_account_class', override: 'hide-in-firm-risk' },
    // Account Origin
    { id: 'wb_account_origin', override: 'hide-in-firm-risk' },
    // Dept Type
    { id: 'wb_dept_type', override: 'hide-in-firm-risk' },

    // order review properties
    { id: 'opt_soft_max_order_notional', override: 'show-in-order-review' },
    { id: 'opt_soft_max_order_size', override: 'show-in-order-review' },
    { id: 'soft_max_order_notional', override: 'show-in-order-review' },
    { id: 'soft_max_order_size', override: 'show-in-order-review' },
];

export interface AdminOMSPropertyFeatures {
    showInFirmInfo: boolean;
    showInAccount: boolean;
    showInFirmRisk: boolean;
    requiredInAccount: boolean;
    showInOrderReview: boolean;
}

export function getAdminOMSPropertyFeatures(
    propertyName: string,
    overrides: AdminOMSPropertyOverrides,
): AdminOMSPropertyFeatures {
    let showInFirmInfo = false;
    let showInAccount = false;
    let showInFirmRisk = true;
    let requiredInAccount = false;
    let showInOrderReview = false;

    for (const def of [...INITIAL_ADMIN_OMS_PROPERTY_OVERRIDES, ...overrides]) {
        if (def.id !== propertyName) continue;
        if (def.override === 'show-in-firm-info') showInFirmInfo = true;
        if (def.override === 'hide-in-firm-info') showInFirmInfo = false;
        if (def.override === 'show-in-account') showInAccount = true;
        if (def.override === 'hide-in-account') showInAccount = false;
        if (def.override === 'show-in-firm-risk') showInFirmRisk = true;
        if (def.override === 'hide-in-firm-risk') showInFirmRisk = false;
        if (def.override === 'required-in-account') requiredInAccount = true;
        if (def.override === 'show-in-order-review') showInOrderReview = true;
    }

    return {
        showInFirmInfo,
        showInAccount,
        showInFirmRisk,
        requiredInAccount,
        showInOrderReview,
    };
}
